﻿import { useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ATTENDANCE_NAG_OPEN_LABEL,
  ATTENDANCE_NAG_REFUSED,
  ATTENDANCE_NAG_SELF_CLEARS,
  AWARD_TODO_OPEN_LABEL,
  attendanceNagHref,
  awardTodoSelfClears,
  isAttendanceNagTaskId,
  isStudentAwardTask,
  useCompleteTask,
  type TaskDoc,
} from '../../lib/tasks';
import { ACTION_REFUSED, actionErrorMessage } from '../../lib/permissionDenied';
import { COMP_MUSIC_OPEN_LABEL, compMusicClassHref, compMusicTaskOfferingId } from '../../lib/compMusicTask';
import { BAG_ITEM_OPEN_LABEL, bagItemGroupHref, bagItemTaskGroupId } from '../../lib/bagItemTask';
import { useMyOpenTasksStillOpenable } from '../../lib/bagItemTaskLive';
import { isRoomRequestTask, parseRoomRequestTask, roomRequestDateKey, stripRoomRequestPayload } from '../../lib/roomRequests';
import { RoomRequestFacts } from '../../components/RoomRequestFacts';
import { cardVariants, enter } from '../../lib/motion';
import logoUrl from '../../assets/logo-the-studio.png';
import { formatDueShort } from './helpers';
import { useAuth } from '../../lib/auth-context';
import { useUserDoc } from '../../lib/users';
import { useHomeAccent } from '../../lib/homeAccent';
import { teacherDisplayName } from '../../lib/teacherTitle';
import { useSeason } from '../../lib/season';
import { seasonGreetingEmoji, useSeasonBadgePickVersion } from '../../lib/seasonBadgePick';
import SeasonBadgePicker from '../../components/SeasonBadgePicker';
import { SeasonHeaderDecoration } from '../../components/SeasonHeaderDecoration';
import { taskActionMeta } from '../../lib/taskSources';
import {
  compactLogoImgStyle,
  dateLineStyle,
  eyebrowStyle,
  greetingSubStyle,
  heroLogoImgStyle,
  heroLogoWrapStyle,
  heroStyle,
  heroTaglineStyle,
  homeTaskHeadingStyle,
  homeTaskListStyle,
  homeTaskRowStyle,
  posterAccentStyle,
  posterGreetingStyle,
  sectionHeaderLabelStyle,
  sectionHeaderRuleStyle,
  sectionHeaderStyle,
} from './styles';

export function CompactHomeLogo() {
  return (
    <a
      href="/"
      aria-label="Home"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        minHeight: 44,
        minWidth: 44,
        padding: '0 var(--space-2)',
        borderRadius: 'var(--radius-md)',
        lineHeight: 0,
      }}
    >
      <img
        className="studio-wordmark-logo"
        src={logoUrl}
        alt="The Studio, Liberty Dance & Glee"
        style={compactLogoImgStyle}
      />
    </a>
  );
}

export function Hero() {
  const reduce = useReducedMotion();
  return (
    <motion.div {...enter(cardVariants, reduce)} style={heroStyle}>
      <h1 style={{ margin: 0, lineHeight: 0 }}>
        <span className="shimmer" style={heroLogoWrapStyle}>
          <img
            className="studio-wordmark-logo"
            src={logoUrl}
            alt="The Studio, Liberty Dance & Glee"
            style={heroLogoImgStyle}
          />
        </span>
      </h1>
      <div style={eyebrowStyle}>The Studio LDG Hub</div>
      <p style={heroTaglineStyle}>
        The private schedule, calendar, and communication home for Studio LDG families, dancers, and staff.
      </p>
    </motion.div>
  );
}

/**
 * A section divider: tracked-out label, then a hairline that fades to nothing.
 *
 * The mockup uses this to separate every block on the home screen, and it is
 * what stops a long page reading as one undifferentiated list of cards.
 */
export function SectionRule({ label, noAccent = false }: { label: string; noAccent?: boolean }) {
  // noAccent: a Director previewing someone else's page must not paint her own colour on it.
  const ownAccent = useHomeAccent();
  const accent = noAccent ? {} as { className?: string; style?: CSSProperties } : ownAccent;
  return (
    <div className={accent.className} style={{ ...sectionHeaderStyle, ...accent.style }}>
      <div style={sectionHeaderLabelStyle}>{label}</div>
      <div style={sectionHeaderRuleStyle} aria-hidden="true" />
    </div>
  );
}

/**
 * "Let's go, Kim" at poster size, over today's date.
 *
 * The first name only, and only when there is one to use: an email address set
 * in 58px uppercase Anton is a wall, not a greeting, so an account with no
 * display name falls back to a plain "Let's go" rather than shouting
 * BOBBIEBFORD@GMAIL.COM at its owner.
 *
 * The date is formatted for a person, not a machine, and it is the reason
 * somebody glancing at the phone in a hallway knows the page is live.
 */
export function HomeGreeting({ roleLabel, sub }: { roleLabel: string; sub?: string }) {
  const { user } = useAuth();
  const { realUserDoc } = useUserDoc();
  const reduce = useReducedMotion();
  const { season } = useSeason();
  useSeasonBadgePickVersion();

  const displayName = (user?.displayName ?? '').trim();
  const firstName = displayName.split(/\s+/)[0] ?? '';
  // Every label the app renders this person by, so the badge pick follows
  // them onto avatars that use the teacher-title name (seasonBadgePick.ts).
  const pickLabels = [displayName, realUserDoc ? teacherDisplayName(realUserDoc) : ''];
  // A Jr Staff is ONE PERSON on either sign-in (CLAUDE.md, DECISIONS 26 and
  // 27). Her dancer sign-in gets the kids' large, changeable badge picker
  // (DECISIONS 117), so her email sign-in, role 'ensemble', gets the same one.
  const jrStaff = realUserDoc?.role === 'ensemble';
  const dateLine = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  // N18: the person's own picked colour (useHomeAccent) tints the date line and
  // the accent word, through the palette helper. None chosen: today's look.
  const accent = useHomeAccent();
  return (
    <motion.div {...enter(cardVariants, reduce)} className={accent.className} style={accent.style as CSSProperties | undefined}>
      <div style={dateLineStyle}>{dateLine}</div>
      <h1 style={{ ...posterGreetingStyle, position: 'relative' }}>
        {/* The garland (Bobbie: "Can you do the lights and spiderwebs on
            the let's as well?") - purely decorative, aria-hidden inside
            itself, so it never touches this heading's accessible name. */}
        {season && <SeasonHeaderDecoration seasonKey={season.key} />}
        Let&apos;s
        <br />
        <span style={posterAccentStyle}>Go</span>
        {firstName ? `, ${firstName}` : ''}
        {/* Whimsical, not spooky - one cute glyph per holiday window (pumpkin,
            turkey, tree, hearts, clover), never anything ghoulish (Bobbie,
            2026-08-17). Windows live in lib/season.tsx; a picked badge
            (seasonBadgePick.ts) personalizes it. */}
        {season ? ` ${seasonGreetingEmoji(season, pickLabels)}` : ''}
      </h1>
      <p style={greetingSubStyle}>
        {roleLabel}
        {sub ? `. ${sub}` : ''}
      </p>
      {/* First home visit inside a live window: pick your badge. Hides
          itself once answered (SeasonBadgePicker), except for a Jr Staff,
          whose picker folds to a Change row like it does on her dancer home. */}
      <SeasonBadgePicker labels={pickLabels} changeable={jrStaff} size={jrStaff ? 'large' : 'regular'} />
    </motion.div>
  );
}

function HomeTaskSummaryRow({ t }: { t: TaskDoc }) {
  const navigate = useNavigate();
  const completeTask = useCompleteTask();
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const lastTapRef = useRef<number>(0);

  const due = formatDueShort(t.dueAt);
  // Some tasks (the monthly Student Awards reminders are the case today)
  // carry no `source` but still have a provable destination baked into
  // their own id - falls back to the plain task list when there is none.
  const action = taskActionMeta(t);
  // A room request's facts render as a labeled card below, never the raw
  // description - the home widget does not load the offerings list, so the
  // card omits the offering name here (still every other fact, real room
  // name, no machine block).
  const roomRequest = parseRoomRequestTask(t);
  const isRoomReq = isRoomRequestTask(t.description);
  // The attendance nag is cleared by taking attendance, never by hand (the
  // rules refuse every client write to it). Mark done here is what put the
  // raw refusal on Kim's home screen on 2026-09-10; its one control opens
  // Attendance on that class and day instead. lib/attendanceNagTask.ts.
  const isNag = isAttendanceNagTaskId(t.id);
  // A comp music to-do (lib/compMusicTask.ts) is finished by posting the
  // music, never by Mark done: its one control opens the class's Music &
  // Video tab instead.
  const compMusicOfferingId = compMusicTaskOfferingId(t);
  // A bag item to-do (lib/bagItemTask.ts) is checked off by dropping the item
  // off on its group page, so its one control here opens that page.
  const bagItemGroupId = bagItemTaskGroupId(t);
  // The monthly award to-do (lib/studentAwardTask.ts, DECISIONS 170) is never
  // marked done by hand: its one control opens Student Awards on its own
  // round (action, below), where her pick or No pick this month checks it off.
  const isAwardTodo = isStudentAwardTask(t);
  const handleTap = () => {
    const now = Date.now();
    if (now - lastTapRef.current < 350) {
      lastTapRef.current = 0;
      navigate('/tasks');
      return;
    }
    lastTapRef.current = now;
    setExpanded((v) => !v);
  };

  const handleComplete = async (e: MouseEvent) => {
    e.stopPropagation();
    setBusy(true);
    setErr(null);
    try {
      await completeTask(t.id);
    } catch (err) {
      setErr(actionErrorMessage(err, isNag ? ATTENDANCE_NAG_REFUSED : ACTION_REFUSED));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleTap}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleTap();
        }
      }}
      style={{
        ...homeTaskRowStyle,
        flexDirection: 'column',
        alignItems: 'stretch',
        cursor: 'pointer',
        background: expanded ? 'rgba(127, 196, 0, 0.08)' : 'transparent',
        userSelect: 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 'var(--space-2)',
        }}
      >
        <span
          style={{
            fontSize: 14,
            color: 'var(--ink)',
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {t.title}
        </span>
        {due.text && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: due.overdue ? 'var(--rose-deep)' : 'var(--ink-soft)',
              flexShrink: 0,
            }}
          >
            {due.text}
          </span>
        )}
      </div>
      {expanded && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            marginTop: 6,
          }}
        >
          {roomRequest ? (
            <RoomRequestFacts details={roomRequest} dense />
          ) : (
            // stripRoomRequestPayload is the safety net for a hand-broken or
            // future-format request block: isRoomRequestTask can be true
            // while parseRoomRequestTask still returns null (see
            // room-requests.test.mjs), and that description must never show
            // its machine block just because the structured card above
            // could not be built from it.
            stripRoomRequestPayload(t.description) && (
              <div style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
                {stripRoomRequestPayload(t.description)}
              </div>
            )
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {isRoomReq ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/calendar/requests');
                }}
                style={{
                  background: 'var(--lime)',
                  color: '#0E1A0A',
                  fontWeight: 800,
                  fontSize: 13,
                  padding: '6px 12px',
                  minHeight: 36,
                  border: 0,
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                }}
              >
                Manage request
              </button>
            ) : isNag ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(attendanceNagHref(t));
                }}
                style={{
                  background: 'var(--lime)',
                  color: '#0E1A0A',
                  fontWeight: 800,
                  fontSize: 13,
                  padding: '6px 12px',
                  minHeight: 'var(--tap-target)',
                  border: 0,
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                }}
              >
                {ATTENDANCE_NAG_OPEN_LABEL}
              </button>
            ) : compMusicOfferingId ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(compMusicClassHref(compMusicOfferingId));
                }}
                style={{
                  background: 'var(--lime)',
                  color: '#0E1A0A',
                  fontWeight: 800,
                  fontSize: 13,
                  padding: '6px 12px',
                  minHeight: 'var(--tap-target)',
                  border: 0,
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                }}
                data-comp-music-open
              >
                {COMP_MUSIC_OPEN_LABEL}
              </button>
            ) : bagItemGroupId ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(bagItemGroupHref(bagItemGroupId));
                }}
                style={{
                  background: 'var(--lime)',
                  color: '#0E1A0A',
                  fontWeight: 800,
                  fontSize: 13,
                  padding: '6px 12px',
                  minHeight: 'var(--tap-target)',
                  border: 0,
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                }}
                data-bag-item-open
              >
                {BAG_ITEM_OPEN_LABEL}
              </button>
            ) : isAwardTodo && action ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(action.href);
                }}
                style={{
                  background: 'var(--lime)',
                  color: '#0E1A0A',
                  fontWeight: 800,
                  fontSize: 13,
                  padding: '6px 12px',
                  minHeight: 'var(--tap-target)',
                  border: 0,
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                }}
                data-award-todo-open
              >
                {AWARD_TODO_OPEN_LABEL}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleComplete}
                disabled={busy}
                style={{
                  background: 'var(--lime)',
                  color: '#0E1A0A',
                  fontWeight: 800,
                  fontSize: 13,
                  padding: '6px 12px',
                  minHeight: 36,
                  border: 0,
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  opacity: busy ? 0.6 : 1,
                }}
              >
                {busy ? 'Marking...' : 'Mark done'}
              </button>
            )}
            {/* Bobbie, 2026-08-05: Kim is deciding whether this time works, and
                she could not see the rest of that day without leaving the card.
                Jumps to the requested day so she can look before she approves.
                Same /calendar?d= convention roomRequestActions.ts already uses. */}
            {isRoomReq && roomRequest && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/calendar?d=${roomRequestDateKey(roomRequest)}`);
                }}
                style={{
                  background: 'transparent',
                  color: 'var(--ink)',
                  border: '1px solid var(--lime)',
                  fontWeight: 800,
                  fontSize: 13,
                  padding: '6px 12px',
                  minHeight: 36,
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                }}
              >
                View on calendar
              </button>
            )}
            {/* On an award to-do the 44px door above IS this door, so the
                36px ghost would be the same button twice. */}
            {!isAwardTodo && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(action ? action.href : '/tasks');
                }}
                style={{
                  background: 'transparent',
                  color: 'var(--ink-soft)',
                  border: '1px solid var(--line)',
                  fontWeight: 600,
                  fontSize: 13,
                  padding: '6px 12px',
                  minHeight: 36,
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                }}
              >
                {action ? action.actionLabel : 'Open in To-dos'}
              </button>
            )}
          </div>
          {isNag && (
            <div style={{ fontSize: 12, color: 'var(--ink-soft)', lineHeight: 1.4 }}>
              {ATTENDANCE_NAG_SELF_CLEARS}
            </div>
          )}
          {isAwardTodo && (
            <div style={{ fontSize: 12, color: 'var(--ink-soft)', lineHeight: 1.4 }}>
              {awardTodoSelfClears(t)}
            </div>
          )}
          <div style={{ fontSize: 10, color: 'var(--ink-soft)' }}>
            Tip: double-tap any task to jump straight to the full list.
          </div>
          {err && <div style={{ fontSize: 12, color: 'var(--rose-deep)' }}>{err}</div>}
        </div>
      )}
    </div>
  );
}

/**
 * @param embedded - rendered inside the "Open to-dos for you" row on the home
 * card, where the row label already says what this is, so the heading would
 * just repeat it. Standalone use keeps the heading.
 */
export function HomeTaskSummary({ embedded = false }: { embedded?: boolean } = {}) {
  // A bag item to-do whose group she can no longer open is left off BEFORE
  // the three rows are cut (useMyOpenTasksStillOpenable; BG-8 refute rounds).
  const { loading, tasks, confirmed } = useMyOpenTasksStillOpenable(3);
  if (loading) return null;
  return (
    <div style={homeTaskListStyle}>
      {!embedded && <div style={homeTaskHeadingStyle}>Your to-dos</div>}
      {tasks.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--ink-soft)', padding: '4px 8px' }}>
          {confirmed ? 'To do List Complete!' : 'Still checking the to-do list...'}
        </div>
      )}
      {tasks.map((t: TaskDoc) => (
        <HomeTaskSummaryRow key={t.id} t={t} />
      ))}
    </div>
  );
}
